/**
 * Media item type for multi-media post support
 */
export interface MediaItem {
  id: string; // UUID for React keys
  type: 'image' | 'video';
  path: string | null; // Server path or 'local-video' for pending upload
  preview: string | null; // Blob URL or API URL (used for the main preview player)
  thumbnailUrl: string | null; // Static image snapshot for thumbnails (avoids rendering full <video>)
  localFile: File | null; // For videos not yet uploaded
  order: number;
  // Video-specific editing state
  videoConfig?: VideoConfig;
  // File metadata
  fileSize?: number | null;
  duration?: number | null;
}

export interface VideoConfig {
  startTime: number;
  endTime: number;
  duration: number;
  captions: VideoCaption[];
  // Desktop and Mobile configurations
  desktopConfig: VideoFormatConfig;
  mobileConfig: VideoFormatConfig;
  unifyFormat: boolean;
}

export interface VideoFormatConfig {
  cropData: CropData | null;
  crop: { x: number; y: number } | null;
  zoom: number;
}

export interface CropData {
  x: number;
  y: number;
  width: number;
  height: number;
  aspect: number;
}

export interface VideoCaption {
  id: string;
  text: string;
  startTime: number;
  endTime: number;
  fontFamily?: string;
  fontSize?: number;
  color?: string;
  backgroundColor?: string;
  position?: 'top' | 'center' | 'bottom';
}

/**
 * Helpers for media items
 */
export function createMediaItem(
  type: 'image' | 'video',
  file?: File,
  path?: string,
  preview?: string
): MediaItem {
  return {
    id: crypto.randomUUID(),
    type,
    path: path || null,
    preview: preview || null,
    thumbnailUrl: type === 'image' ? (preview || null) : null, // Images use preview directly; videos get a snapshot later
    localFile: file || null,
    order: 0,
    fileSize: file?.size || null,
    duration: null,
  };
}

/**
 * Generate a static thumbnail image from a video file.
 * Captures a frame at 0.5s (or first available frame) and returns a data URL.
 */
export function generateVideoThumbnail(videoSrc: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.preload = 'metadata';

    video.onloadeddata = () => {
      // Seek to 0.5s or to 0 if video is shorter
      video.currentTime = Math.min(0.5, video.duration || 0);
    };

    video.onseeked = () => {
      try {
        const canvas = document.createElement('canvas');
        // Use a small size for thumbnail -- 192px is enough for a 96px thumbnail at 2x
        const maxSize = 192;
        const scale = Math.min(maxSize / video.videoWidth, maxSize / video.videoHeight, 1);
        canvas.width = video.videoWidth * scale;
        canvas.height = video.videoHeight * scale;

        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
          resolve(dataUrl);
        } else {
          reject(new Error('Could not get canvas context'));
        }
      } catch (err) {
        reject(err);
      }
    };

    video.onerror = () => reject(new Error('Failed to load video for thumbnail'));

    // Timeout after 5 seconds
    setTimeout(() => reject(new Error('Thumbnail generation timed out')), 5000);

    video.src = videoSrc;
  });
}

export function createDefaultVideoConfig(duration: number): VideoConfig {
  return {
    startTime: 0,
    endTime: duration,
    duration,
    captions: [],
    desktopConfig: {
      cropData: null,
      crop: null,
      zoom: 1,
    },
    mobileConfig: {
      cropData: null,
      crop: null,
      zoom: 1,
    },
    unifyFormat: true,
  };
}
